import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, LinearProgress, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import PauseOutlined from '@mui/icons-material/PauseCircleOutlineOutlined';
import PlayOutlined from '@mui/icons-material/PlayCircleOutlineOutlined';
import { useRouteLoaderData } from 'react-router';
import { GlassCard } from '@/components/GlassCard';
import { getConnectionStats } from '@/features/stats/api';
import type { ConnectionStats } from '@/features/stats/api';
import type { UserProfile } from '@/auth/session';

const POLL_MS = 5000;

/** Severity of the connection load. The share is always spelled out as text
 *  beside the meter, so colour is never the only thing carrying the state. */
function severity(used: number, max: number): 'success' | 'warning' | 'error' {
  const share = max > 0 ? used / max : 0;
  if (share >= 0.9) return 'error';
  if (share >= 0.7) return 'warning';
  return 'success';
}

function Tile({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Box sx={{ minWidth: 140 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Box>
  );
}

export default function DashboardPage() {
  const user = useRouteLoaderData('protected') as UserProfile;
  // The endpoint is admin-only — it names every schema on the shared server, not
  // just this console's companies — so a plain operator must not poll it and sit
  // behind a 403 on the landing page.
  const isAdmin = user?.role === 'admin';
  const [stats, setStats] = useState<ConnectionStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [live, setLive] = useState(true);

  useEffect(() => {
    let active = true;
    // The query behind this scans the processlist and hits the registry once per
    // schema, so on a loaded server — exactly when someone is watching — it can
    // outlast the interval. Firing anyway would compound the load that caused
    // the slowness and let an older answer land on top of a newer one.
    let inFlight = false;
    async function tick() {
      if (inFlight) return;
      inFlight = true;
      try {
        const res = await getConnectionStats();
        if (!active) return;
        setStats(res);
        setError(null);
        setUpdatedAt(new Date());
      } catch {
        // Keep the last good numbers on screen — a blip should not blank a page
        // meant to be watched, and the next tick usually recovers on its own.
        if (active) setError('ດຶງສະຖິຕິບໍ່ໄດ້');
      } finally {
        inFlight = false;
      }
    }
    // Stopped means stopped: no interval and no request. Resuming reads once
    // straight away rather than leaving a stale number up for five seconds.
    if (!live || !isAdmin) return;
    void tick();
    const id = setInterval(() => { void tick(); }, POLL_MS);
    return () => { active = false; clearInterval(id); };
  }, [live, isAdmin]);

  const used = stats?.threadsConnected ?? 0;
  const max = stats?.maxConnections ?? 0;
  const share = max > 0 ? Math.min(100, (used / max) * 100) : 0;
  const attributed = stats?.perDb.reduce((n, d) => n + d.conn, 0) ?? 0;
  // `threadsConnected` and the per-database rows come from two statements read a
  // moment apart, so their sums drift by a connection or two as clients come and
  // go. Taking the unattributed count as the remainder against the headline —
  // rather than as the scan's own NULL-schema group — is what keeps the table
  // adding up to the number printed above it.
  //
  // A partial view is a different thing: there the difference is other clients'
  // connections, not sockets holding no schema, so the table totals only what it
  // can see and the banner above explains the rest.
  const unattributed = stats === null ? 0
    : stats.partial ? stats.unattributed : Math.max(0, used - attributed);
  const listed = stats?.partial === true ? attributed + unattributed : used;

  if (!isAdmin) {
    return (
      <Stack spacing={3}>
        <Typography variant="h3">ສະບາຍດີ, {user?.username}</Typography>
        <Typography variant="body2" color="text.secondary">
          ສະຖິຕິການເຊື່ອມຕໍ່ຖານຂໍ້ມູນເປີດໃຫ້ສະເພາະຜູ້ດູແລລະບົບ
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h3">ສະບາຍດີ, {user?.username}</Typography>

      <GlassCard>
        <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h6">ການເຊື່ອມຕໍ່ຖານຂໍ້ມູນ</Typography>
            <Typography variant="body2" color="text.secondary">
              ນັບທັງເຊີບເວີ — ລວມການເຊື່ອມຕໍ່ຂອງລະບົບບັນຊີ ບໍ່ແມ່ນສະເພາະ console ນີ້
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={live ? <PauseOutlined /> : <PlayOutlined />}
            onClick={() => {
              // A stopped page never ticks again, so an error left showing would
              // sit there for good with nothing able to clear it.
              setError(null);
              setLive((v) => !v);
            }}
          >
            {live ? 'ຢຸດອັບເດດ' : 'ອັບເດດຕໍ່'}
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {stats?.partial && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            ເຫັນການເຊື່ອມຕໍ່ບໍ່ຄົບ — ຜູ້ໃຊ້ຖານຂໍ້ມູນຂອງ console ບໍ່ມີສິດ PROCESS
            ຈຶ່ງເຫັນສະເພາະການເຊື່ອມຕໍ່ຂອງຕົນເອງ. ຕົວເລກລວມຂ້າງເທິງຍັງຖືກຕ້ອງ
          </Alert>
        )}

        <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap', rowGap: 2, mb: 2 }}>
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="body2" color="text.secondary">ກຳລັງເຊື່ອມຕໍ່</Typography>
            <Typography sx={{ fontSize: 48, lineHeight: 1.1, fontWeight: 600 }}>{used}</Typography>
            <Typography variant="body2" color="text.secondary">
              ຈາກສູງສຸດ {max} ({share.toFixed(0)}%)
            </Typography>
          </Box>
          <Tile label="ກຳລັງເຮັດວຽກ" value={stats?.threadsRunning ?? 0} />
          <Tile
            label="ຖານຂໍ້ມູນທີ່ມີການໃຊ້"
            value={stats?.perDb.length ?? 0}
            // Same incomplete source as the table below it, so it must not sit
            // among the server-wide totals unqualified.
            hint={stats?.partial ? 'ນັບໄດ້ບໍ່ຄົບ' : undefined}
          />
        </Stack>

        <LinearProgress
          variant="determinate"
          value={share}
          color={severity(used, max)}
          sx={{ height: 8, borderRadius: 4, mb: 1 }}
        />
        <Typography variant="caption" color="text.secondary">
          {updatedAt ? `ອັບເດດ ${updatedAt.toLocaleTimeString()}` : 'ກຳລັງໂຫຼດ...'}
          {!live && ' · ຢຸດຢູ່'}
        </Typography>
      </GlassCard>

      <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
        <Typography variant="h6" sx={{ p: 2, pb: 1 }}>ບໍລິສັດທີ່ກຳລັງໃຊ້ຢູ່</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>pathName</TableCell>
                <TableCell>ຊື່ບໍລິສັດ</TableCell>
                <TableCell align="right">ການເຊື່ອມຕໍ່</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats === null ? (
                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>ກຳລັງໂຫຼດ...</TableCell></TableRow>
              ) : stats.perDb.length === 0 && unattributed === 0 ? (
                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>ບໍ່ມີການເຊື່ອມຕໍ່</TableCell></TableRow>
              ) : (
                <>
                  {stats.perDb.map((d) => (
                    <TableRow key={d.db} hover>
                      <TableCell>{d.db}</TableCell>
                      <TableCell>{d.name ?? '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{d.conn}</TableCell>
                    </TableRow>
                  ))}
                  {unattributed > 0 && (
                    // Belongs to no company, but without it the headline total
                    // and this table simply do not add up on screen.
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>ບໍ່ໄດ້ເລືອກຖານຂໍ້ມູນ (pool ທີ່ວ່າງຢູ່)</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                        {unattributed}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell colSpan={2} align="right" sx={{ fontWeight: 600, borderBottom: 0 }}>ລວມ</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, borderBottom: 0, fontVariantNumeric: 'tabular-nums' }}>
                      {listed}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </GlassCard>
    </Stack>
  );
}
